import { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Download } from "lucide-react";

import { Button } from "./Button";
import { UnifiedModal } from "./UnifiedModal";
import { EXPORT_DATASETS, formatExportDate, runExport } from "../services/csvExportService";

/**
 * Asks whether to download only what was created since the last download or everything.
 * `summary` is one item of GET /exports/ `datasets`.
 */
export function IncrementalExportModal({ open, onClose, summary, onExported, extraActions }) {
  const [runningMode, setRunningMode] = useState(null);
  const [error, setError] = useState(null);

  if (!summary) return null;

  const config = EXPORT_DATASETS[summary.dataset];
  const lastExport = summary.last_export;

  const handleExport = async (mode) => {
    setRunningMode(mode);
    setError(null);
    try {
      const count = await runExport(summary.dataset, mode);
      onExported?.(count, mode);
      onClose();
    } catch (err) {
      console.error("Error descargando CSV:", err);
      setError("No se ha podido generar el CSV. Inténtalo de nuevo.");
    } finally {
      setRunningMode(null);
    }
  };

  const busy = runningMode !== null;

  return (
    <UnifiedModal
      open={open}
      onClose={busy ? undefined : onClose}
      title={`Descargar ${config.label.toLowerCase()}`}
      maxWidth="sm"
      footer={
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" gap={1}>
          {extraActions}
          <Button variant={lastExport ? "outline" : "default"} onClick={() => handleExport("full")} disabled={busy}>
            {runningMode === "full" ? "Descargando..." : `Descargar todo (${summary.total_count})`}
          </Button>
          {lastExport && (
            <Button onClick={() => handleExport("incremental")} disabled={busy || summary.pending_count === 0}>
              <Download size={16} />
              {runningMode === "incremental"
                ? "Descargando..."
                : `Descargar solo los nuevos (${summary.pending_count})`}
            </Button>
          )}
        </Stack>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {lastExport ? (
          <>
            <Typography variant="body2">
              La última descarga se hizo el <strong>{formatExportDate(lastExport.exported_at)}</strong>
              {lastExport.exported_by_name ? ` por ${lastExport.exported_by_name}` : ""} e incluye todo
              hasta esa fecha.
            </Typography>
            <Typography variant="body2">
              {summary.pending_count === 0
                ? "No se ha creado nada desde entonces."
                : `Desde entonces se han creado ${summary.pending_count} registro(s). ¿Quieres descargar solo esos?`}
            </Typography>
          </>
        ) : (
          <Typography variant="body2">
            Todavía no se ha descargado nunca. Se descargará el listado completo y la próxima vez podrás
            bajar solo lo creado desde hoy.
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </Box>
    </UnifiedModal>
  );
}
